/**
 * Resolve a caller-supplied "where to go next" value to a safe in-app path.
 *
 * Three call sites used to hand-roll this as `startsWith('/') && !startsWith('//')`,
 * which has two problems:
 *
 *   - it misses `/\evil.com` and `/<tab>/evil.com`, which the WHATWG URL parser
 *     resolves to a different origin. react-router's `replace` then calls
 *     history.replaceState with a cross-origin URL, which throws SecurityError
 *     and, with no ErrorBoundary in the tree, takes the whole app to a white
 *     screen;
 *   - it rejects absolute same-origin URLs outright, and three components
 *     (`AotdIrlClaim`, `AotdIrlDevices`, `OAuthConsentPage`) pass
 *     `window.location.href`. Those users silently lost their return target,
 *     which for the AOTD-IRL pairing flow meant losing a one-shot token from the
 *     URL and having to re-scan the frame.
 *
 * Parsing against the current origin handles both, and normalising to
 * path + search + hash keeps the result safe to hand to react-router.
 *
 * @returns the in-app path, or null when the value is missing or points elsewhere
 */
export function toSameOriginPath(value: string | null | undefined): string | null {
    if (!value) return null;

    try {
        // NOTE: no decodeURIComponent here. URLSearchParams.get() has already
        // percent-decoded once; decoding again mangles `?q=a%26b` into two
        // parameters and throws URIError on a legitimate bare `%` in a path
        // (a recipe slug like `/cookbook/100%-chocolate-cake` is reachable).
        const url = new URL(value, window.location.origin);
        if (url.origin !== window.location.origin) return null;

        const path = `${url.pathname}${url.search}${url.hash}`;
        return path.startsWith('/') ? path : null;
    } catch {
        return null;
    }
}

/** True when the value resolves to a path on this origin. */
export function isSameOriginPath(value: string | null | undefined): boolean {
    return toSameOriginPath(value) !== null;
}
